import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { watermarkChoiceMessages } from '../choice/watermarkChoiceMessages';
import { useOpenWatermarkOffer } from '../useOpenWatermarkOffer';
import { useUpsellShown } from '../useUpsellShown';
import { useWatermarkSurface } from '../useWatermarkSurface';
import { watermarkFileMessages as messages } from './watermarkFileMessages';
import { WatermarkThumbTile } from './WatermarkThumbTile';
import { PaidPlanLine } from './PaidPlanLine';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The banner draws this many thumbnails, then "+N" (design a4). */
export const BANNER_THUMBS = 3;

interface WatermarkBannerProps {
  className?: string;
}

/**
 * The banner at the top of the Kit tab, the Posts page and the release Posts tab (design:
 * trial-upsell a4): the newest watermarked files, the count of the workspace and "Remove the
 * watermark" with the price of the cheapest paid plan. It hides at 0 files, on a paid plan and for
 * the demo artist. A member reads the owner note. The clean-copies sentence shows only while the
 * licence can get its run. On a phone it is one slim row (QA 2026-10-28, mob-14): no thumbnails
 * and no body line, so the posts stay above the fold.
 */
const WatermarkBannerComponent: React.FC<WatermarkBannerProps> = ({ className }) => {
  const intl = useIntl();
  const { applies, plan, isMember, summary, trialDaysRemaining } = useWatermarkSurface();
  const openOffer = useOpenWatermarkOffer();
  const remove = useCallback(() => openOffer('banner'), [openOffer]);
  const shows = applies && summary.files > 0;
  useUpsellShown('banner', shows);
  const thumbs = useMemo(() => summary.thumbs.slice(0, BANNER_THUMBS), [summary.thumbs]);
  if (!shows) {
    return null;
  }
  const more = summary.files - thumbs.length;
  const status =
    plan === 'trial' && trialDaysRemaining !== null
      ? intl.formatMessage(messages.daysLeft, { days: trialDaysRemaining })
      : intl.formatMessage(messages.onFree);

  return (
    <div className={clsx(styles.banner, className)} data-testid="watermark-banner">
      {thumbs.length > 0 && (
        <div className={styles.strip} aria-hidden="true">
          {thumbs.map((thumb) => (
            <WatermarkThumbTile key={thumb.url} url={thumb.url} shape={thumb.shape} />
          ))}
          {more > 0 && <div className={styles.tileMore}>+{more}</div>}
        </div>
      )}
      <div className={styles.bannerText}>
        <b>{withBrandWord(intl.formatMessage(messages.bannerTitle, { count: summary.files }))}</b>
        <small>
          {intl.formatMessage(messages.bannerUnit)}{' '}
          {summary.cleanCopiesAvailable
            ? intl.formatMessage(messages.bannerBody, { count: summary.files })
            : intl.formatMessage(messages.bannerBodyNoCopies)}
        </small>
      </div>
      <div className={styles.bannerRight}>
        {isMember ? (
          <span className={styles.days} data-testid="watermark-banner-owner-note">
            {intl.formatMessage(watermarkChoiceMessages.ownerNote)}
          </span>
        ) : (
          <>
            <SMLButton variant="primary" onClick={remove}>
              {intl.formatMessage(watermarkChoiceMessages.remove)}
            </SMLButton>
            <PaidPlanLine className={styles.days} />
          </>
        )}
        <span className={clsx(styles.days, styles.bannerStatus)}>{status}</span>
      </div>
    </div>
  );
};

export const WatermarkBanner = TypedMemo(WatermarkBannerComponent);
